/**
 * Save-gate validation for the automation-rule builder (SE-5506).
 *
 * A pure predicate over the form: the builder's Save button is enabled only
 * when every field REQUIRED by the current Kind / trigger / action is filled,
 * and every condition row is complete for its value kind. Kept React-free so
 * the enable/disable contract can be asserted table-driven in a unit test.
 * @module features/player-kanban/utils/automationRuleValidation
 */

import type {
  AutomationRuleConditionForm,
  AutomationRuleFormState,
} from "../types/automationRules";
import {
  CONDITION_VALUE_KIND,
  actionNeedsTag,
  actionNeedsTargetStage,
  triggerNeedsStage,
} from "./automationRuleOptions";

/**
 * Whether one condition row is complete for its value kind.
 * @param condition - The condition to check.
 * @returns True when the condition has the value(s) its kind requires.
 */
const isConditionComplete = (
  condition: AutomationRuleConditionForm
): boolean => {
  const kind = CONDITION_VALUE_KIND[condition.conditionType];
  if (kind === "stage") return Boolean(condition.kanbanPhaseId);
  if (kind === "text") return condition.textValue.trim().length > 0;
  // numeric — needs a comparator and a value.
  return (
    Boolean(condition.comparator) && condition.numericValue.trim().length > 0
  );
};

/**
 * Whether the form is complete enough to save.
 * @param form - The builder form state.
 * @returns True when required fields + every condition are filled.
 */
export const isAutomationRuleFormComplete = (
  form: AutomationRuleFormState
): boolean => {
  if (!form.name.trim()) return false;

  if (form.kind === "guard") {
    // A guard must name the stage it guards.
    if (!form.triggerKanbanPhaseId) return false;
  } else {
    if (triggerNeedsStage(form.triggerType) && !form.triggerKanbanPhaseId) {
      return false;
    }
    if (actionNeedsTargetStage(form.actionType) && !form.targetKanbanPhaseId) {
      return false;
    }
    if (actionNeedsTag(form.actionType) && !form.kanbanTagId) {
      return false;
    }
  }

  return form.conditions.every(isConditionComplete);
};
